import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '@/store';
import { fetchMyProviders, setProviderFilter } from '@/features/provider/providerSlice';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Search, Filter, X, Edit, ExternalLink, Mail } from 'lucide-react';
import { tunisianGovernorates, governorateCities } from '@/data/tunisian-governorates';
import { useToast } from '@/components/ui/use-toast';
import ProviderLogo from "@/components/common/ProviderLogo";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const Pagination = ({ currentPage, totalPages, onPageChange }: PaginationProps) => {
  const pages = Array.from({ length: totalPages }, (_, i) => i + 1);

  return (
    <div className="flex justify-center mt-6 space-x-2">
      <Button variant="outline" size="sm" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}>
        Previous
      </Button>
      {pages.map(page => (
        <Button
          key={page}
          variant={currentPage === page ? "default" : "outline"}
          size="sm"
          onClick={() => onPageChange(page)}
        >
          {page}
        </Button>
      ))}
      <Button variant="outline" size="sm" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}>
        Next
      </Button>
    </div>
  );
};

const MyProvidersList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { providers, paginationMeta, loading, error, filter } = useSelector((state: RootState) => state.provider);

  // Local state for filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedOwnership, setSelectedOwnership] = useState('all');
  const [selectedCountry, setSelectedCountry] = useState('Tunisia');
  const [selectedCity, setSelectedCity] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedState, setSelectedState] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  // Initialize providers list on component mount
  useEffect(() => {
    // Reset to default filter and fetch providers
    const initialFilter = {
      search: null,
      type: null,
      ownership: null,
      country: 'Tunisia',
      state: null,
      city: null,
      category: null,
      page: 1,
      limit: 10
    };

    dispatch(setProviderFilter(initialFilter));
    dispatch(fetchMyProviders(initialFilter) as any);
  }, [dispatch]);

  // Handle search
  const handleSearch = () => {
    const newFilter = {
      ...filter,
      search: searchTerm || null,
      page: 1 // Reset to first page on new search
    };
    dispatch(setProviderFilter(newFilter));
    dispatch(fetchMyProviders(newFilter) as any);
  };

  // Apply all filters
  const applyFilters = () => {
    const newFilter = {
      search: searchTerm || null,
      type: selectedType === 'all' ? null : selectedType,
      ownership: selectedOwnership === 'all' ? null : selectedOwnership,
      country: selectedCountry || null,
      city: selectedCity === 'all' ? null : selectedCity,
      state: selectedState === 'all' ? null : selectedState,
      category: selectedCategory === 'all' ? null : selectedCategory,
      page: 1,
      limit: filter.limit,
    };
    dispatch(setProviderFilter(newFilter));
    dispatch(fetchMyProviders(newFilter) as any);
    toast({ title: "Filters Applied", description: "Provider list updated.", duration: 2000 });
  };

  // Reset filters
  const resetFilters = () => {
    setSearchTerm('');
    setSelectedType('all');
    setSelectedOwnership('all');
    setSelectedCountry('Tunisia');
    setSelectedCity('all');
    setSelectedCategory('all');
    setSelectedState('all');
    const resetFilter = { search: null, type: null, ownership: null, country: 'Tunisia', city: null, state: null, category: null, page: 1, limit: 10 };
    dispatch(setProviderFilter(resetFilter));
    dispatch(fetchMyProviders(resetFilter) as any);
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    dispatch(setProviderFilter({ ...filter, page }));
    dispatch(fetchMyProviders({ ...filter, page }) as any);
  };

  // Handle state change
  const handleStateChange = (value: string) => {
    setSelectedState(value);
    setSelectedCity('all');
  };

  // Format provider type for display
  const formatProviderType = (type: string) => {
    if (!type) return '';
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  // View provider details
  const viewProviderDetails = (id: number | string) => {
    navigate(`/admin/my-providers/${id}`);
  };

  // Edit provider
  const editProvider = (id: number | string) => {
    navigate(`/admin/providers/${id}/edit`);
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2">My Providers</h1>
        <p className="text-gray-600">Manage the educational providers you've created</p>
      </div>

      {/* Search and filter bar */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4 items-center">
          <div className="relative flex-grow">
            <Input
              type="text"
              placeholder="Search providers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          </div>

          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="min-w-[120px]"
          >
            {showFilters ? <X className="mr-2 h-4 w-4" /> : <Filter className="mr-2 h-4 w-4" />}
            {showFilters ? "Hide Filters" : "Show Filters"}
          </Button>

          <Button onClick={handleSearch} className="min-w-[100px]">
            Search
          </Button>
        </div>

        {/* Expanded filters */}
        {showFilters && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="type">Type</Label>
              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger id="type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="university">University</SelectItem>
                  <SelectItem value="college">College</SelectItem>
                  <SelectItem value="institute">Institute</SelectItem>
                  <SelectItem value="academy">Academy</SelectItem>
                  <SelectItem value="school">School</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="ownership">Ownership</Label>
              <Select value={selectedOwnership} onValueChange={setSelectedOwnership}>
                <SelectTrigger id="ownership">
                  <SelectValue placeholder="Select ownership" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Ownerships</SelectItem>
                  <SelectItem value="public">Public</SelectItem>
                  <SelectItem value="private">Private</SelectItem>
                  <SelectItem value="non-profit">Non-Profit</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="country">Country</Label>
              <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                <SelectTrigger id="country">
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Tunisia">Tunisia</SelectItem>
                  {/* Add more countries as needed */}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="state">State/Governorate</Label>
              <Select value={selectedState} onValueChange={handleStateChange}>
                <SelectTrigger id="state">
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All States</SelectItem>
                  {tunisianGovernorates.map(state => (
                    <SelectItem key={state} value={state}>{state}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="city">City</Label>
              <Select
                value={selectedCity}
                onValueChange={setSelectedCity}
                disabled={selectedState === 'all'}
              >
                <SelectTrigger id="city">
                  <SelectValue placeholder={selectedState === 'all' ? "Select state first" : "Select city"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cities</SelectItem>
                  {selectedState !== 'all' && governorateCities[selectedState]?.map(city => (
                    <SelectItem key={city} value={city}>{city}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger id="category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="engineering">Engineering</SelectItem>
                  <SelectItem value="medicine">Medicine</SelectItem>
                  <SelectItem value="business">Business</SelectItem>
                  <SelectItem value="arts">Arts</SelectItem>
                  <SelectItem value="science">Science</SelectItem>
                  <SelectItem value="technology">Technology</SelectItem>
                  <SelectItem value="humanities">Humanities</SelectItem>
                  <SelectItem value="education">Education</SelectItem>
                  <SelectItem value="law">Law</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-1 md:col-span-2 lg:col-span-3 flex justify-end space-x-2 mt-4">
              <Button variant="outline" onClick={resetFilters}>
                Reset Filters
              </Button>
              <Button onClick={applyFilters}>
                Apply Filters
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Loading state */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-2 text-lg">Loading providers...</span>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          <p className="font-medium">Error loading providers</p>
          <p className="text-sm">{error}</p>
          <Button
            variant="outline"
            className="mt-2 text-red-600 border-red-300 hover:bg-red-50"
            onClick={resetFilters}
          >
            Try Again
          </Button>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && providers.length === 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">No providers found</h3>
          <p className="text-gray-600 mb-4">
            {searchTerm || selectedType !== 'all' || selectedOwnership !== 'all' || selectedCity !== 'all' || selectedState !== 'all' || selectedCategory !== 'all'
              ? "Try adjusting your filters or search terms"
              : "You haven't created any providers yet"}
          </p>
          <Button onClick={() => navigate('/providers/create')}>
            Create New Provider
          </Button>
        </div>
      )}

      {/* Providers grid */}
      {!loading && !error && providers.length > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {providers.map((provider) => (
              <Card key={provider.id} className="overflow-hidden hover:shadow-md transition-shadow h-full">
                <div className="h-40 bg-gray-100 overflow-hidden">
                  {provider.logoUrl ? (
                    <div className="w-full h-full flex items-center justify-center p-4">
                      <ProviderLogo
                        filename={provider.logoUrl}
                        alt={`${provider.name} logo`}
                        className="max-h-full max-w-full object-contain"
                      />
                    </div>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-primary/10">
                      <span className="text-primary font-bold text-4xl">
                        {provider.name.charAt(0)}
                      </span>
                    </div>
                  )}
                </div>
                <CardContent className="p-4">
                  <div>
                    <h3 className="font-semibold text-lg truncate mb-1" title={provider.name}>
                      {provider.name}
                    </h3>
                    <p className="text-sm text-gray-500 mb-2">{provider.city}, {provider.country}</p>
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded">
                        {formatProviderType(provider.type)}
                      </span>
                      <span className="bg-gray-100 text-gray-800 text-xs px-2 py-0.5 rounded">
                        {provider.ownership}
                      </span>
                      {provider.category && (
                        <span className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded">
                          {provider.category}
                        </span>
                      )}
                    </div>

                    <div className="text-sm text-gray-600 mb-3 line-clamp-2" title={provider.description || "No description available"}>
                      {provider.description || "No description available"}
                    </div>

                    <div className="text-xs text-gray-500 mb-4">
                      <div className="flex items-center">
                        <span className="font-medium">Location:</span>
                        <span className="ml-1">{provider.city}, {provider.country}</span>
                      </div>
                      {provider.email && (
                        <div className="flex items-center mt-1">
                          <span className="font-medium">Email:</span>
                          <span className="ml-1 truncate">{provider.email}</span>
                        </div>
                      )}
                      {provider.createdAt && (
                        <div className="flex items-center mt-1">
                          <span className="font-medium">Created:</span>
                          <span className="ml-1">{new Date(parseInt(provider.createdAt)).toLocaleDateString()}</span>
                        </div>
                      )}
                    </div>

                    {provider.imageUrls && provider.imageUrls.length > 1 && (
                      <div className="mt-3 text-xs text-gray-500">
                        <span className="font-medium">{provider.imageUrls.length} images available</span>
                      </div>
                    )}

                    <div className="flex justify-between mt-4 pt-3 border-t border-gray-100">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex items-center"
                        onClick={() => viewProviderDetails(provider.id)}
                      >
                        <ExternalLink className="h-4 w-4 mr-1" />
                        View
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        className="flex items-center"
                        onClick={() => editProvider(provider.id)}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>

                      {provider.email && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex items-center"
                          onClick={() => window.open(`mailto:${provider.email}`)}
                        >
                          <Mail className="h-4 w-4 mr-1" />
                          Contact
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {paginationMeta && paginationMeta.totalPages > 1 && (
            <Pagination
              currentPage={paginationMeta.currentPage}
              totalPages={paginationMeta.totalPages}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}
    </div>
  );
};

export default MyProvidersList;
